// src/pages/Invoices.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiFileText,
  FiSend,
  FiEdit2,
  FiTrash2,
  FiRefreshCcw,
  FiPercent,
  FiCalendar,
  FiHash,
  FiDollarSign,
} from 'react-icons/fi';

import api from '../api/axios';
import EditInvoiceModal from '../components/EditInvoiceModal';

import {
  listInvoices,
  sendInvoice,
  voidInvoice,
  writeOffInvoice,
  deleteInvoice,
} from '../services/invoiceService';

// ✅ Money utils (avoid RangeError / invalid codes + default to company currency)
import { formatMoney, safeCurrency, getCompanyCurrency } from '../utils/money';

/* ----------------------------- helpers ----------------------------- */

// ✅ Shared money helpers (company-currency default)
const fmtMoney = (n, c) => formatMoney(n, safeCurrency(c || getCompanyCurrency()));

// 🔧 Date helpers: treat YYYY-MM-DD as date-only (UTC) to avoid off-by-one
const fmtDateOnlyUTC = (value) => {
  if (!value) return '—';
  try {
    let date;
    if (typeof value === 'string') {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        const d = Number(m[3]);
        date = new Date(Date.UTC(y, mo - 1, d));
      } else {
        date = new Date(value);
      }
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return '—';
  }
};

const resolveId = (row) => row?.invoiceId || row?._id || row?.id;

const STATUS_OPTIONS = [
  { label: 'Unpaid', value: 'unpaid' },
  { label: 'Partial', value: 'partial' },
  { label: 'Paid', value: 'paid' },
  { label: 'Overdue', value: 'overdue' },
];

const LIFECYCLE_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Void', value: 'void' },
  { label: 'Written Off', value: 'written_off' },
];

const STATUS_CHIP = {
  unpaid: 'bg-amber-100 text-amber-700 border border-amber-200',
  partial: 'bg-blue-100 text-blue-700 border border-blue-200',
  paid: 'bg-green-100 text-green-700 border border-green-200',
  overdue: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const LIFECYCLE_CHIP = {
  draft: 'bg-slate-100 text-slate-800 border border-slate-200',
  sent: 'bg-purple-100 text-purple-800 border border-purple-200',
  void: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
  written_off: 'bg-orange-100 text-orange-800 border border-orange-200',
};

const SORTABLE = [
  { id: 'createdAt', label: 'Created' },
  { id: 'updatedAt', label: 'Updated' },
  { id: 'issueDate', label: 'Issue Date' },
  { id: 'dueDate', label: 'Due Date' },
  { id: 'invoiceNumber', label: 'Invoice #' },
  { id: 'totalAmount', label: 'Total' },
  { id: 'balanceDue', label: 'Balance' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* ----------------------------- small portal menu ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate,
  onClose,
  onView,
  onOpenPdf,
  onQuickEdit,
  onVoid,
  onWriteOff,
  onDelete,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;

    const menuW = 240;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle((s) => ({ ...s, top, left, origin }));

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = resolveId(row);
  const lc = String(row?.lifecycleStatus || '').toLowerCase();
  const locked = ['void', 'written_off', 'sent'].includes(lc);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50"
        onClick={() => {
          onClose();
          onView(id);
        }}
      >
        View details
      </button>

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={async () => {
          onClose();
          await onOpenPdf(id);
        }}
      >
        <FiFileText /> Open PDF
      </button>

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={() => {
          onClose();
          if (locked) onView(id);
          else onQuickEdit(id);
        }}
      >
        <FiEdit2 /> {locked ? 'View' : 'Edit'}
      </button>

      <div className="my-1 h-px bg-slate-100" />

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left ${
          canMutate ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onVoid(row);
        }}
      >
        VOID
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left ${
          canMutate ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onWriteOff(row);
        }}
      >
        Write-off
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${
          canMutate ? 'hover:bg-rose-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onDelete(row);
        }}
      >
        <FiTrash2 className="inline -mt-0.5 mr-1" /> Archive
      </button>
    </div>,
    document.body
  );
}

/* ----------------------------- main ----------------------------- */

const Invoices = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const [role, setRole] = useState('viewer');
  const canMutate = role === 'admin' || role === 'staff';

  // Query state
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [lifecycleStatus, setLifecycleStatus] = useState('all');
  const [dateField, setDateField] = useState('issueDate');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Data
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 25 });
  const [loading, setLoading] = useState(true);

  // UI state
  const [editingId, setEditingId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
    setLifecycleStatus('all');
    setDateField('issueDate');
    setDateFrom('');
    setDateTo('');
    setSortBy('createdAt');
    setSortDir('desc');
    setLimit(25);
    setPage(1);
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/profile');
      const r = res?.data?.data || res?.data;
      setRole(r?.role || 'viewer');
      localStorage.setItem('profile', JSON.stringify(r || {}));
    } catch {
      try {
        const p = JSON.parse(localStorage.getItem('profile') || '{}');
        setRole(p?.role || 'viewer');
      } catch {
        setRole('viewer');
      }
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search.trim() || undefined,
        status: status === 'all' ? undefined : status,
        lifecycleStatus: lifecycleStatus === 'all' ? undefined : lifecycleStatus,
        dateField,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        sortBy,
        sortOrder: sortDir,
      };

      const { items, meta: m } = await listInvoices(params);

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[Invoices] list payload (service):', { items, meta: m });
      }

      setRows(Array.isArray(items) ? items : []);

      setMeta({
        page: Number(m?.page ?? 1),
        limit: Number(m?.limit ?? limit),
        total: Number.isFinite(Number(m?.total))
          ? Number(m.total)
          : (Array.isArray(items) ? items.length : 0),
        totalPages:
          (Number.isFinite(Number(m?.totalPages)) && Number(m.totalPages) > 0)
            ? Number(m.totalPages)
            : (Number.isFinite(Number(m?.total)) && Number(m.total) > 0
                ? Math.ceil(Number(m.total) / Number(m?.limit ?? limit ?? 25))
                : 1),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to load invoices');
      setRows([]);
      setMeta({ page: 1, totalPages: 1, total: 0, limit });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, lifecycleStatus, dateField, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  /* ----------------------------- actions ----------------------------- */

  const openPdf = useCallback(async (id) => {
    try {
      if (!id) throw new Error('Missing invoice id');

      const row = rows.find((r) => resolveId(r) === id) || {};
      const fileName = `${row.invoiceNumber || 'invoice'}.pdf`.replace(/[^\w.-]+/g, '_');

      const resp = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob',
        params: { download: 1, v: row?.updatedAt ? Number(new Date(row.updatedAt)) : Date.now() },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();

      if (ct.includes('application/json')) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || '{}');
          throw new Error(json?.error || 'Server error while generating PDF');
        } catch {
          throw new Error(text || 'Server error while generating PDF');
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0) throw new Error('Empty PDF response');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(`PDF error: ${e?.message || 'Unable to download PDF'}`);
    }
  }, [rows]);

  const doSend = async (row) => {
    if (!canMutate) return;
    const suggested = row?.clientSnapshot?.email || '';
    const to = prompt('Send to (comma-separated emails):', suggested);
    if (to === null) return;
    try {
      await sendInvoice(resolveId(row), {
        to: to
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        subject: `Invoice ${row.invoiceNumber}`,
        message: '',
        attachPdf: true,
        force: true,
      });
      toast.success(`Sent invoice ${row.invoiceNumber}`);
      fetchInvoices();
    } catch {
      toast.error('Failed to send invoice');
    }
  };

  const doVoid = async (row) => {
    if (!canMutate) return;
    const reason = prompt('Reason for void (optional):', '');
    if (reason === null) return;
    try {
      await voidInvoice(resolveId(row), { reason });
      toast.success('Invoice voided');
      fetchInvoices();
    } catch {
      toast.error('Failed to void invoice');
    }
  };

  const doWriteOff = async (row) => {
    if (!canMutate) return;
    const reason = prompt('Reason for write-off (required):', '');
    if (!reason?.trim()) {
      toast.info('Write-off cancelled (reason required).');
      return;
    }
    try {
      await writeOffInvoice(resolveId(row), { reason });
      toast.success('Invoice written off');
      fetchInvoices();
    } catch {
      toast.error('Failed to write off invoice');
    }
  };

  const doDelete = async (row) => {
    if (!canMutate) return;
    if (!window.confirm('Archive this invoice?\nIf payments exist, deletion is blocked.')) return;
    try {
      await deleteInvoice(resolveId(row));
      toast.success('Invoice archived');
      fetchInvoices();
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === 'CANNOT_DELETE_WITH_PAYMENTS') {
        toast.warn('Invoice has payments. Use Void or Write-off instead.');
      } else {
        toast.error('Failed to archive invoice');
      }
    }
  };

  /* ----------------------------- derived ----------------------------- */

  const isLocked = (row) =>
    ['void', 'written_off', 'sent'].includes(String(row?.lifecycleStatus || '').toLowerCase());

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  const statusCounts = useMemo(() => {
    const c = { unpaid: 0, partial: 0, paid: 0, overdue: 0 };
    for (const r of rows) {
      const k = String(r.status || '').toLowerCase();
      if (c[k] !== undefined) c[k]++;
    }
    return c;
  }, [rows]);

  const lifecycleCounts = useMemo(() => {
    const c = { draft: 0, sent: 0, void: 0, written_off: 0 };
    for (const r of rows) {
      const k = String(r.lifecycleStatus || '').toLowerCase();
      if (c[k] !== undefined) c[k]++;
    }
    return c;
  }, [rows]);

  const computed = useMemo(() => {
    const t = Number(meta?.total ?? 0);
    const l = Number(meta?.limit ?? limit ?? 25);
    const p = Number(meta?.page ?? page ?? 1);
    const tp = Number(meta?.totalPages);
    const totalPages =
      Number.isFinite(tp) && tp > 0 ? tp : (t > 0 && l > 0 ? Math.ceil(t / l) : 1);
    return { total: t, limit: l, page: p, totalPages };
  }, [meta.total, meta.limit, meta.page, meta.totalPages, limit, page]);

  const companyCurrency = getCompanyCurrency();

  /* ----------------------------- UI ----------------------------- */

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      <div className="rounded-2xl mb-6 border border-slate-200 bg-white shadow-sm">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FiHash />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Invoices</h1>
              </div>
              <p className="text-slate-600 mt-1">
                Track invoices, send PDFs, and manage the invoice lifecycle.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/invoices/new')}
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-black/90 transition"
              >
                <FiPlus className="text-lg" /> New Invoice
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiDollarSign /> Page Total
              </div>
              <div className="text-lg font-bold text-slate-900">
                {fmtMoney(rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0), companyCurrency)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiPercent className="inline -mt-0.5 mr-1" />
                Overdue
              </div>
              <div className="text-lg font-bold text-slate-900">{statusCounts.overdue || 0}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Paid</div>
              <div className="text-lg font-bold text-slate-900">{statusCounts.paid || 0}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Sent</div>
              <div className="text-lg font-bold text-slate-900">{lifecycleCounts.sent || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-96">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search invoice #, client, notes…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div className="flex flex-1 sm:flex-none gap-2 justify-stretch sm:justify-end">
              {/* Status */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by status"
                >
                  {['all', ...STATUS_OPTIONS.map((s) => s.value)].map((s) => (
                    <option key={s} value={s}>
                      {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Lifecycle */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={lifecycleStatus}
                  onChange={(e) => {
                    setLifecycleStatus(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by lifecycle"
                >
                  {['all', ...LIFECYCLE_OPTIONS.map((s) => s.value)].map((s) => (
                    <option key={s} value={s}>
                      {s === 'all'
                        ? 'All Lifecycles'
                        : s === 'written_off'
                        ? 'Written Off'
                        : s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Date field + range */}
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateField}
                  onChange={(e) => {
                    setDateField(e.target.value);
                    setPage(1);
                  }}
                  title="Date field"
                >
                  <option value="issueDate">Issue</option>
                  <option value="dueDate">Due</option>
                </select>
                <input
                  type="date"
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  title="From"
                />
                <input
                  type="date"
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  title="To"
                />
              </div>

              {/* Sort / size */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  title="Sort by"
                >
                  {SORTABLE.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value);
                    setPage(1);
                  }}
                  title="Sort direction"
                >
                  <option value="desc">Newest</option>
                  <option value="asc">Oldest</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  title="Rows per page"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <button
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
                onClick={resetFilters}
                title="Reset filters"
              >
                <FiRefreshCcw /> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="invoiceNumber" label="Invoice #" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Client</th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="issueDate" label="Issue" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="dueDate" label="Due" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-left font-bold">Lifecycle</th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="totalAmount" label="Total" />
                </th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="balanceDue" label="Balance" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-4">
                      <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-14">
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <FiFileText className="text-slate-400" />
                      </div>
                      <div className="text-slate-600 font-semibold">No invoices found</div>
                      <div className="text-slate-500 text-sm mb-4">
                        Adjust filters or create a new invoice.
                      </div>
                      <button
                        onClick={() => navigate('/invoices/new')}
                        className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-black/90 transition"
                      >
                        <FiPlus /> New Invoice
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const id = resolveId(row);
                  const locked = isLocked(row);
                  return (
                    <tr key={id} className="group transition hover:bg-blue-50/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => navigate(`/invoices/${id}`)}
                          title="View details"
                        >
                          {row.invoiceNumber}
                        </button>
                        {/* single-currency UX: show company currency (or safe row.currency if you still store it) */}
                        <div className="text-xs text-slate-500">
                          {safeCurrency(row.currency || companyCurrency)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          className="text-slate-900 font-medium hover:underline"
                          onClick={() => navigate(`/clients/${row.clientId}`)}
                          title="Open client"
                        >
                          {row.clientName || '—'}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.issueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.dueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[row.status] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus === 'written_off'
                            ? 'Written Off'
                            : row.lifecycleStatus?.charAt(0).toUpperCase() + row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.totalAmount, row.currency || companyCurrency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.balanceDue, row.currency || companyCurrency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={menuRow && resolveId(menuRow) === id ? 'true' : 'false'}
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No invoices found.</div>
          ) : (
            rows.map((row) => {
              const id = resolveId(row);
              const locked = isLocked(row);
              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        <button
                          className="hover:underline"
                          onClick={() => navigate(`/invoices/${id}`)}
                          title="View details"
                        >
                          {row.invoiceNumber}
                        </button>{' '}
                        • {row.clientName || '—'}
                      </div>
                      <div className="text-sm text-slate-600 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Issue {fmtDateOnlyUTC(row.issueDate)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Due {fmtDateOnlyUTC(row.dueDate)}
                        </span>
                      </div>
                      <div className="mt-1 flex gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[row.status] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || '—'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus === 'written_off'
                            ? 'Written Off'
                            : row.lifecycleStatus?.charAt(0).toUpperCase() + row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">
                        {fmtMoney(row.totalAmount, row.currency || companyCurrency)}
                      </div>
                      <div className="text-xs text-slate-500">
                        Balance {fmtMoney(row.balanceDue, row.currency || companyCurrency)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => navigate(`/invoices/${id}`)}
                    >
                      View
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={async () => {
                        await openPdf(id);
                      }}
                    >
                      <FiFileText className="inline -mt-0.5 mr-1" />
                      PDF
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => {
                        if (locked) navigate(`/invoices/${id}`);
                        else setEditingId(id);
                      }}
                    >
                      <FiEdit2 className="inline -mt-0.5 mr-1" />
                      {locked ? 'View' : 'Edit'}
                    </button>

                    {canMutate && (
                      <>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={() => doSend(row)}
                        >
                          <FiSend className="inline -mt-0.5 mr-1" />
                          Send
                        </button>
                        <button className="px-3 py-1.5 rounded-xl border text-sm" onClick={() => doVoid(row)}>
                          VOID
                        </button>
                        <button className="px-3 py-1.5 rounded-xl border text-sm" onClick={() => doWriteOff(row)}>
                          WO
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                          onClick={() => doDelete(row)}
                        >
                          <FiTrash2 className="inline -mt-0.5 mr-1" />
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{computed.page}</span> of{' '}
            <span className="font-semibold">{computed.totalPages}</span> •{' '}
            <span className="font-semibold">{computed.total}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={computed.page <= 1 || loading}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{computed.page}</span> of{' '}
              <span className="font-semibold">{computed.totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(computed.totalPages || 1, p + 1))}
              disabled={computed.page >= (computed.totalPages || 1) || loading}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={canMutate}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onView={(id) => navigate(`/invoices/${id}`)}
          onOpenPdf={openPdf}
          onQuickEdit={(id) => setEditingId(id)}
          onVoid={doVoid}
          onWriteOff={doWriteOff}
          onDelete={doDelete}
        />
      )}

      {/* Edit modal */}
      {editingId && (
        <EditInvoiceModal
          key={editingId}
          isOpen={!!editingId}
          invoiceId={editingId}
          onClose={() => setEditingId(null)}
          onUpdated={() => {
            setEditingId(null);
            fetchInvoices();
            toast.success('Invoice updated');
          }}
        />
      )}
    </div>
  );
};

export default Invoices;
